import {
    Children,
    useEffect,
    useMemo,
    useRef,
    useState,
    type HTMLAttributes,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from 'react';
import type { IconNames } from '~/components/ui/Icon';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import Tooltip from '~/components/ui/Tooltip';
import '~/styles/components/new/chip.scss';
import type { DataTestAttributes } from '~/utils/qa';
import {
    classNames,
    surfaceColorVar,
    textColorVar,
    type SurfaceColor,
    type TextColor,
} from '~/utils/styles';
import { z } from '~/utils/zIndex';
import type Avatar from './Avatar';
import type { AvatarProps } from './Avatar';
import IconAction from './IconAction';

type ExtractSupportColor<T> = T extends `support-${infer Name}-${string}` ? Name : never;
export type BackgroundSupportColor = ExtractSupportColor<SurfaceColor> | 'grey';

export type ChipBaseProps = {
    className?: string;
    children?: ReactNode;
    size?: 'small' | 'medium' | 'large';
    visualSupport?: ReactElement<typeof Avatar | typeof Icon>; // This can be an icon or avatar
    controlSupport?: ReactElement<typeof IconAction>;
    variant?: 'filled' | 'outlined' | 'floating';
    color?: BackgroundSupportColor;
} & HTMLAttributes<HTMLDivElement>;

function ChipBase({
    color = 'grey',
    variant = 'filled',
    size = 'medium',
    visualSupport,
    controlSupport,
    className = '',
    children,
    ...props
}: ChipBaseProps) {
    const surfaceColorName = `support-${color}-light`;
    const textColorName = `support-${color}`;
    const surfaceStyle = {
        '--chip-background-color': `var(${surfaceColorVar(surfaceColorName as SurfaceColor)})`,
        '--chip-text-color': `var(${textColorVar(textColorName as TextColor)})`,
    };

    return (
        <div
            className={classNames({
                [`chip chip-variant-${variant} chip-size-${size}`]: true,
                [className]: Boolean(className),
            })}
            style={surfaceStyle}
            {...props}>
            {visualSupport && <div className='chip-visual-support'>{visualSupport}</div>}
            <span className='chip-content'>{children}</span>
            {controlSupport && <div className='chip-control-support'>{controlSupport}</div>}
        </div>
    );
}

// Chip Meta
export type ChipMetaProps = Omit<ChipBaseProps, 'visualSupport'> & {
    icon?: IconNames;
    avatar?: ReactElement<AvatarProps>;
    chipType?: 'meta' | 'choice' | 'tag' | 'input'; // internal use only
    tooltipText?: string;
};
export default function Chip({
    icon,
    avatar,
    color = 'grey',
    chipType = 'meta',
    className = '',
    tooltipText,
    controlSupport,
    ...props
}: ChipMetaProps) {
    const visualSupportAvatar = avatar;

    const visualSupportIcon = icon ? (
        <Icon
            icon={icon}
            size={props.size === 'small' ? 12 : 16}
            colorVar={
                //  if color is gray or gradient, use grey
                color === 'grey' || color === 'gradient' ? `support-grey` : `support-${color}-dark`
            }
        />
    ) : undefined;

    const infoControlSupport = tooltipText ? (
        <IconAction
            title={tooltipText ?? ''}
            colorVar='core-secondary'
            icon={RemixIconNames.RiInformationLine}
        />
    ) : undefined;
    // when control support is specified, we can't use icon with tooltip
    const finalControlSupport = controlSupport ?? infoControlSupport;

    return (
        <ChipBase
            {...props}
            color={color}
            className={classNames({
                [`chip-type-${chipType}`]: true,
                [className]: Boolean(className),
            })}
            visualSupport={visualSupportAvatar || visualSupportIcon}
            controlSupport={finalControlSupport}
        />
    );
}

// Chip Choice
export type ChipChoiceProps = Omit<ChipMetaProps, 'chipType' | 'variant'> & {
    selected?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    'data-test-component'?: string;
    'data-test-name'?: string;
};

export function ChipChoice({
    onClick,
    selected,
    disabled,
    children,
    'data-test-component': testComponent = 'new/Chip/Choice',
    'data-test-name': testName,
    ...chipProps
}: ChipChoiceProps) {
    const inputRef = useRef<HTMLInputElement>(null!);

    return (
        // linking the click to the input ref handles the disabled state
        <div
            role='button'
            onClick={(e) => {
                e.stopPropagation();
                inputRef.current.click();
            }}
            data-test-component={testComponent}
            data-test-name={testName}>
            <Chip
                {...chipProps}
                chipType='choice'
                variant='outlined'>
                {children}
                <input
                    ref={inputRef}
                    onClick={() => onClick?.()}
                    type='checkbox'
                    checked={selected}
                    disabled={disabled}
                    readOnly
                />
            </Chip>
        </div>
    );
}

// Chip Tag
export type ChipTagProps = Omit<ChipMetaProps, 'visualSupport' | 'variant' | 'chipType'> & {
    variant?: 'filled' | 'floating';
    'data-test-component'?: string;
};

export function ChipTag({
    variant,
    size = 'small',
    className = '',
    children,
    'data-test-component': dataTestComponent = 'new/Chip/Tag',
    ...chipProps
}: ChipTagProps) {
    return (
        <Chip
            {...chipProps}
            data-test-component={dataTestComponent}
            variant={variant}
            chipType='tag'
            className={classNames({ 'chip-tag': true, [className]: Boolean(className) })}
            size={size}>
            {children}
        </Chip>
    );
}

// Chip Input
export type ChipInputProps = Omit<
    ChipMetaProps,
    'visualSupport' | 'variant' | 'chipType' | 'color'
> & {
    onClose: () => void;
};

export function ChipInput({ onClose, children, ...chipProps }: ChipInputProps) {
    return (
        <Chip
            {...chipProps}
            variant='filled'
            chipType='input'
            controlSupport={<CloseIconAction onClick={onClose} />}>
            {children}
        </Chip>
    );
}

function CloseIconAction({ onClick }: { onClick: () => void }) {
    return (
        <div
            role='button'
            className='flex'>
            <IconAction
                className='chip-tag-close'
                title=''
                icon={RemixIconNames.RiCloseCircleFill}
                colorVar={'core-secondary'}
                hoverColorVar={'core-primary'}
                onClick={() => onClick?.()}
            />
        </div>
    );
}

type SkeletonProps = {
    count?: number;
    key_prefix?: string;
    size?: ChipBaseProps['size'];
};
Chip.Skeleton = function ChipSkeleton({
    count = 1,
    key_prefix = 'chip_skeleton',
    size = 'small',
}: SkeletonProps) {
    return Array.from({ length: count }).map((_, i) => (
        <div
            key={`${key_prefix}_${i}`}
            className={`chip chip-skeleton chip-size-${size}`}
            data-test-component='new/Chip/Skeleton'>
            &nbsp;
        </div>
    ));
};

export type ChipListBaseProps = {
    children: ReactNode | ReactNode[];
    align?: 'left' | 'center' | 'right';
    limit?: number;
    className?: string;
    stacked?: boolean;
    counterVariant?: 'filled' | 'outlined';
} & DataTestAttributes;
type OverflowInfo = {
    overflows: boolean;
    overflowContent: string[];
} & DataTestAttributes;

function useOverflow(ref: RefObject<HTMLDivElement>): OverflowInfo {
    const [overflows, setOverflows] = useState<boolean>(false);
    // content for the tooltip
    const [overflowContent, setOverflowContent] = useState<string[]>([]);

    const checkOverflow = () => {
        const chipList = ref.current;
        if (!chipList) return;
        const chips = chipList.querySelectorAll<HTMLElement>('.chip:not(.chip-more)'); // exclude counter chip
        if (chips.length === 0) return;
        const chipMoreWrapper = chipList.querySelector('.chip-list-more-wrapper') as HTMLElement;
        const hasManualLimit = chipMoreWrapper.classList.contains('chip-list-limited');
        const chipMore = chipList.querySelector<HTMLElement>('.chip-more') as HTMLElement;
        const previousInlineDisplay = chipMoreWrapper.style.display;

        const listRect = chipList.getBoundingClientRect();

        const measureCounterWidth = (counterLabel: string) => {
            if (!chipMore) return 0;

            // Measure outside the observed subtree to avoid retriggering MutationObserver.
            const ghostCounter = chipMore.cloneNode(true) as HTMLElement;
            ghostCounter.style.position = 'absolute';
            ghostCounter.style.visibility = 'hidden';
            ghostCounter.style.pointerEvents = 'none';
            ghostCounter.style.display = 'flex';
            ghostCounter.style.left = '-9999px';
            ghostCounter.style.top = '0';
            const chipContent = ghostCounter.querySelector('.chip-content');
            if (chipContent) chipContent.textContent = counterLabel;
            document.body.appendChild(ghostCounter);
            const measuredWidth = ghostCounter.getBoundingClientRect().width;
            ghostCounter.remove();
            return measuredWidth;
        };

        const getHiddenChips = (reservedCounterWidth: number) =>
            [...chips].filter((chip) => {
                const chipRect = chip.getBoundingClientRect();

                // If only the very last chip would be hidden by the counter itself, avoid showing +1.
                const isLastChip = chips.length > 1 && chip === chips[chips.length - 1];
                const visibleRight = Math.round(
                    listRect.right - (isLastChip && !hasManualLimit ? 0 : reservedCounterWidth),
                );
                const fullyVisible =
                    chipRect.left >= listRect.left && Math.round(chipRect.right) <= visibleRight;
                return !fullyVisible;
            });

        try {
            let hiddenChips = getHiddenChips(0);
            if (hiddenChips.length > 0) {
                const chipMoreWidth = measureCounterWidth(`+${hiddenChips.length}`);
                hiddenChips = getHiddenChips(chipMoreWidth);
            }

            setOverflows(hiddenChips.length > 0);
            setOverflowContent(hiddenChips.map((chip) => chip.textContent || ''));
        } finally {
            chipMoreWrapper.style.display = previousInlineDisplay;
        }
    };

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => requestAnimationFrame(checkOverflow));
        const mutationObserver = new MutationObserver(() => requestAnimationFrame(checkOverflow));

        if (ref.current) {
            mutationObserver.observe(ref.current, {
                childList: true,
                subtree: true,
            });
            resizeObserver.observe(ref.current);
        }
        checkOverflow(); // initial check
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    return { overflows, overflowContent };
}

function AutoOverflowHandler({
    ref,
    setOverflowInfo,
}: {
    ref: RefObject<HTMLDivElement>;
    setOverflowInfo: (info: OverflowInfo) => void;
}) {
    const { overflows, overflowContent } = useOverflow(ref);

    useEffect(() => {
        setOverflowInfo({ overflows, overflowContent });
    }, [overflows, overflowContent]);

    return null;
}

function ChipListComponent({
    overflows,
    overflowContent,
    children = [],
    limit = 0,
    ['data-test-component']: dataTestComponent = 'new/Chip/List',
    ['data-test-name']: dataTestName,
}: OverflowInfo & {
    children: ReactNode | [];
    limit?: number;
}) {
    return (
        <div
            className='chip-list-aligner'
            data-test-component={dataTestComponent}
            data-test-name={dataTestName}>
            {limit ? Children.toArray(children).slice(0, limit) : children}
            <div
                className={classNames({
                    'chip-list-more-wrapper': true,
                    'chip-list-limited': limit > 0 && limit < Children.toArray(children).length,
                    overflowing: overflows,
                })}>
                <Tooltip title={overflowContent.join(' · ')}>
                    <ChipBase
                        className='chip-list-more chip-more'
                        size={
                            (Children.toArray(children)[0] as ReactElement<ChipBaseProps>)?.props
                                .size || 'small'
                        }>
                        +{overflowContent.length || 1} {/* never show +0 */}
                    </ChipBase>
                </Tooltip>
            </div>
        </div>
    );
}

const useManualLimit = (children: ReactNode | ReactNode[], limit: number) => {
    const moreCount = Array.isArray(children) ? children.length - (limit || children.length) : 0;
    const childrenArray = Children.toArray(children) as ReactElement<ChipBaseProps>[];
    let hiddenChildren: ReactElement<ChipBaseProps>[] = [];

    if (limit && childrenArray.length > limit) {
        hiddenChildren = childrenArray.slice(limit);
    }
    return useMemo<OverflowInfo>(
        () => ({
            overflows: moreCount > 0,
            overflowContent: hiddenChildren.map((child) => child.props.children as string),
        }),
        [moreCount],
    );
};
function ChipListBase({
    children,
    align = 'left',
    className = '',
    limit = 0,
    stacked = false,
    counterVariant = 'filled',
    ['data-test-component']: dataTestComponent = 'new/Chip/List',
    ['data-test-name']: dataTestName,
}: ChipListBaseProps) {
    const ref = useRef<HTMLDivElement>(null!);
    const manualOverflowInfo = useManualLimit(children, limit);

    const [{ overflows, overflowContent }, setOverflowInfo] =
        useState<OverflowInfo>(manualOverflowInfo);

    return (
        <div
            ref={ref}
            className={classNames({
                [`chip-list chip-list-align-${align} chip-list-counter-variant-${counterVariant}`]: true,
                'chip-list-stacked': stacked,
                [className]: Boolean(className),
            })}
            data-test-component={dataTestComponent}
            data-test-name={dataTestName}>
            <ChipListComponent
                overflows={overflows || manualOverflowInfo.overflows}
                overflowContent={
                    overflows
                        ? (!stacked ? overflowContent : []).concat(
                              manualOverflowInfo.overflowContent,
                          )
                        : manualOverflowInfo.overflowContent
                }
                limit={limit || 0}>
                {children}
            </ChipListComponent>

            {!stacked && (
                <AutoOverflowHandler
                    ref={ref}
                    setOverflowInfo={setOverflowInfo}
                />
            )}
        </div>
    );
}

// Use it to group ChipInput, ChipTag and ChipMeta with multiple display configs
export type ChipListProps = Omit<ChipListBaseProps, 'children'> & {
    children: ReactElement<ChipBaseProps>[] | ReactElement<SkeletonProps>;
};
export function ChipList(props: ChipListProps) {
    return <ChipListBase {...props} />;
}

// ChipRadioGroup works like a radio input, receives options and renders the chips allowing one selection at a time.
export type ChipChoiceOption = Omit<ChipChoiceProps, 'onClick' | 'children'> & {
    label: string;
    value?: string | number;
    icon?: IconNames;
    disabled?: boolean;
};

export type ChipRadioGroupProps = {
    options: ChipChoiceOption[];
    value?: string | number;
    onChange?: (option: ChipChoiceOption) => void;
    children?: ReactNode;
    id?: string;
    className?: string;
    'data-test-component'?: string;
    'data-test-name'?: string;
    size?: ChipChoiceProps['size'];
} & Pick<ChipListBaseProps, 'align'>;
export function ChipRadioGroup({
    options,
    value,
    onChange,
    align,
    id,
    children: addOns,
    className = '',
    size = 'large',
    'data-test-component': testComponent = 'new/Chip/RadioGroup',
    'data-test-name': testName,
}: ChipRadioGroupProps) {
    return (
        <ChipListBase
            className={classNames({ 'chip-group': true, [className]: Boolean(className) })}
            align={align}
            stacked
            data-test-component={testComponent}
            data-test-name={testName}>
            <FocusableInputHelper id={id} />
            {options.map((option) => {
                const { label: optionLabel, value: optionValue } = option;
                return (
                    <ChipChoice
                        key={option.value ?? option.label}
                        size={size}
                        {...option}
                        role='radio'
                        selected={
                            value !== undefined ? value === optionValue : optionLabel === value
                        }
                        onClick={() => onChange?.(option)}
                        data-test-name={option.value?.toString() ?? option.label}>
                        {optionLabel}
                    </ChipChoice>
                );
            })}
            {addOns}
        </ChipListBase>
    );
}

// ChipCheckGroup works like a checkbox input, receives options and renders the chips allowing multiple selections.
export type ChipCheckGroupProps = {
    options: ChipChoiceOption[];
    values: (string | number)[];
    onChange?: (option: ChipChoiceOption) => void;
    children?: ReactNode;
    'data-test-component'?: string;
    'data-test-name'?: string;
    id?: string;
    className?: string;
} & Pick<ChipListBaseProps, 'align'>;
export function ChipCheckGroup({
    options,
    values,
    onChange,
    align,
    children: addOns,
    'data-test-component': testComponent = 'new/Chip/CheckGroup',
    'data-test-name': testName,
    id,
    className = '',
}: ChipCheckGroupProps) {
    return (
        <ChipListBase
            className={classNames({ 'chip-group': true, [className]: Boolean(className) })}
            align={align}
            stacked
            data-test-component={testComponent}
            data-test-name={testName}>
            <FocusableInputHelper id={id} />
            {options.map((option) => (
                <ChipChoice
                    key={option.value ?? option.label}
                    size='large'
                    {...option}
                    selected={values.includes(option.value ?? option.label)}
                    onClick={() => onChange?.(option)}
                    data-test-name={option.value?.toString() ?? option.label}>
                    {option.label}
                </ChipChoice>
            ))}
            {addOns}
        </ChipListBase>
    );
}

// helper to allow form label to set forcus on first chip of the group
function FocusableInputHelper({ id }: { id?: string }) {
    const ref = useRef<HTMLInputElement>(null!);

    if (!id) return null;

    const focusFirstChip = () => {
        const firstChip = ref.current
            .closest('.chip-group')
            ?.querySelector<HTMLElement>('.chip:not(.chip-more) input');

        firstChip?.focus();
    };
    return (
        <input
            ref={ref}
            style={{
                position: 'absolute',
                zIndex: z.background,
                width: 1,
                height: 1,
                opacity: 0,
            }}
            id={id}
            name={id}
            onFocus={focusFirstChip}
        />
    );
}
